import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { CodeTypeModel }                  from '../../core/models/code-type';
import { LinkComponent }                  from '../../table/cell-components/actions/link.component';
import { Table }                          from '../../table/types/table';
import { TableQueryParameters }           from '../../table/types/table-query-parameters';
import { TableService }                   from '../../table/table.service';
import { TransactionCenterFiltersModel }  from '../typings/transaction-center.filters';
import { TransactionCenterOrderModel }    from './typings/transaction-center.order';
import { TransactionCenterOrdersService } from './transaction-center.orders.service';
import { TransactionCenterService }       from '../transaction-center.service';

@Component({
  selector   : 'wor-transaction-center-orders',
  template: require('./transaction-center.orders.component.html')
})
export class TransactionCenterOrdersComponent implements OnDestroy, OnInit {
  @Input() orders  : Array<TransactionCenterOrderModel> = [];
  @Input() statuses: Array<CodeTypeModel>               = [];

  broadcastKey  = this.ordersService.broadcastKey;
  tableCacheKey = this.ordersService.tableCacheKey;

  filters : TransactionCenterFiltersModel = this.transactionCenterService.clearCachedWildcardDates(
    this.tableService.setFiltersFromCache(this.tableCacheKey, {
      endDate        : null,
      outstandingOnly: false,
      startDate      : null,
      status         : '*',
      value          : ''
    }),
    [ 'endDate', 'startDate' ]
  );

  tableSettings : Table = {
    broadcast: this.broadcastKey,
    cache    : {
      enabled: true,
      key    : this.tableCacheKey
    },
    columns: [
      {
        field : 'order_number',
        header: {
          text: this.translateService.instant('PURCHASE_ORDER_SPACE.ORDER_NUMBER')
        }
      },
      {
        field : 'order_date',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DATE')
        },
        pipe: 'globalDate'
      },
      {
        component: {
          bindings: {
            inputs: {
              model: 'vendor_company_name'
            },
            outputs: {
              onClick: event => this.viewVendor(event)
            }
          },
          enabled: true,
          factory: LinkComponent
        },
        field : 'vendor_company_name',
        header: {
          text: this.translateService.instant('VENDOR_SPACE.SUPPLIER')
        },
        sortable: true
      },
      {
        field : 'status',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.STATUS')
        },
        pipe: 'purchaseOrderStatus'
      },
      {
        component: {
          bindings: {
            inputs: {
              model: 'job_card_number'
            },
            outputs: {
              onClick: event => this.viewJobCard(event)
            }
          },
          enabled: true,
          factory: LinkComponent
        },
        field : 'job_card_number',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.ATTACHED_JOB_CARD')
        },
        sortable: true
      },
      {
        classes: 'text-right',
        field  : 'total',
        header : {
          classes: 'text-right',
          text   : this.translateService.instant('GENERAL_SPACE.FIELD.SUBTOTAL')
        },
        pipe: 'globalCurrency'
      },
      {
        classes: 'text-right',
        field  : 'total_including_tax',
        header : {
          classes: 'text-right',
          text   : this.translateService.instant('GENERAL_SPACE.FIELD.TOTAL')
        },
        pipe: 'globalCurrency'
      }
    ],
    empty: {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_ORDERS_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
      pageSizes : [ 10, 25, 50 ],
      queryCount: () => this.ordersService.getCount(this.filters)
    },
    rows: {
      highlight: {
        enabled: true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.tableCacheKey, {
      direction: 'desc',
      field    : 'order_date'
    })
  };

  searchSubscription: Subscription;

  constructor (
    private ordersService           : TransactionCenterOrdersService,
    private router                  : UIRouter,
    private tableService            : TableService,
    private transactionCenterService: TransactionCenterService,
    private translateService        : TranslateService
  ) {}

  ngOnDestroy () : void {
    this.searchSubscription?.unsubscribe();
  }

  ngOnInit () : void {
    this.transactionCenterService.setSearch(this.filters.value || '');

    this.searchSubscription = this.transactionCenterService.searchChanges
      .subscribe(value => {
        if ( value !== null && value !== this.filters.value ) {
          this.filters.value = value;

          this.filterTable();
        }
      });
  }

  filterTable () : void {
    if ( window[ this.broadcastKey ] ) {
      window[ this.broadcastKey ](this.filters);
    }
  }

  getOrders ( params : TableQueryParameters ) : Promise<Array<TransactionCenterOrderModel>> {
    return this.ordersService.get(this.filters, params)
      .then(( orders : Array<TransactionCenterOrderModel> ) => {
        this.orders = orders;

        return orders;
      });
  }

  viewJobCard ( order : TransactionCenterOrderModel ) : void {
    if ( order.invoice_id ) {
      this.router.stateService.go('app.invoice', {
        customer_id: order.customer_id,
        id         : order.invoice_id,
        vehicle_id : order.vehicle_id
      });
    }
  }

  viewOrder ( order : TransactionCenterOrderModel ) : void {
    this.router.stateService.go('app.purchase-order', {
      id       : order.id,
      vendor_id: order.vendor_id
    });
  }

  viewVendor ( order : TransactionCenterOrderModel ) : void {
    this.router.stateService.go('app.vendor', {
      id: order.vendor_id
    });
  }
}
